import type { ItemResponse } from '@finn-no/mobility-item-insertion-shared';
import { getAt } from '@finn-no/mobility-item-insertion-shared';
import { MessageBus } from '@podium/browser';
import { useEffect, useRef } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { getHuckConfig } from '../utils/getHuckConfig.js';

const eventMap: { [key: string]: string } = {
  model_and_make: 'pf-model-and-make-id',
  'price.net': 'pf-price',
  year: 'pf-year',
  'engine.effect': 'pf-effect',
  mileage: 'pf-mileage',
  first_registration: 'pf-first-registration',
  'repairs.has_undergone_repairs': 'pf-has-undergone-repairs',
};

export const useMessageBus = (methods: UseFormReturn<ItemResponse['data']>) => {
  const config = getHuckConfig();
  const initialDataPublished = useRef(false);

  const channel = 'classified-innfinn-channel';
  const messageBus = new MessageBus();

  const publishData = (data: FieldValues) => {
    for (const dotPath of Object.keys(eventMap)) {
      const value = getAt(data, dotPath);
      const topic = eventMap[dotPath];

      messageBus.publish(channel, topic, value);
    }
  };

  useEffect(() => {
    if (config.model !== 'car-used') return;

    // Avoid sending the data on every re-render
    if (!initialDataPublished.current) {
      const initialData = methods.getValues();
      publishData(initialData);
      initialDataPublished.current = true;
    }

    let previousData = methods.getValues();

    const subscription = methods.watch(() => {
      const currentData = methods.getValues();
      let shouldPublish = false;

      for (const dotPath of Object.keys(eventMap)) {
        const currentValue = getAt(currentData, dotPath);
        const previousValue = getAt(previousData, dotPath);

        if (currentValue !== previousValue) {
          shouldPublish = true;
        }
      }

      if (shouldPublish) {
        publishData(currentData);
        previousData = currentData;
      }
    });

    return () => subscription?.unsubscribe();
  }, [methods.watch]);
};
